/**
 * The startup list (F7).
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 6.2
 */
import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';

import type { StartupSummary } from '@nexus/contracts';

import { BandBadge } from '../../components/domain/BandBadge';
import { ConflictDialog } from '../../components/domain/ConflictDialog';
import { Money } from '../../components/domain/Money';
import { StageBadge } from '../../components/domain/StageBadge';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import type { Column } from '../../components/ui/data-table-types';
import { DataTable } from '../../components/ui/DataTable';
import { ExportButton } from '../../components/ui/ExportButton';
import { PageHead } from '../../components/ui/PageHead';
import { Pagination } from '../../components/ui/Pagination';
import { useAuth } from '../../lib/auth-context';
import { formatNumber } from '../../lib/format';
import { isApiError, ProblemType } from '../../lib/problem';
import { queryKeys } from '../../lib/query-keys';
import { ROUTES, startupPath } from '../../lib/routes';
import { useConflict } from '../../lib/use-conflict';
import { useTableState } from '../../lib/use-table-state';

import { useChangeStage, useStartups } from './api/startups.queries';
import type { PipelineView } from './components/pipeline-views';
import { StartupBoard } from './components/StartupBoard';
import { StartupFilters } from './components/StartupFilters';
import { ViewToggle } from './components/ViewToggle';

/**
 * Query parameters that survive "Clear filters".
 *
 * `view` rides in the URL alongside the filters so a link opens on the right
 * view, but it is not a filter — clearing must not switch the user back to the
 * table.
 */
const PRESERVED_KEYS = ['view'];

/** Sort fields the API allow-lists (see `STARTUP_SORT_FIELDS` on the server). */
const SORT_FIELDS = [
  'name',
  'stage',
  'priorityBand',
  'createdAt',
  'updatedAt',
  'stageEnteredAt',
  'nextActionDueDate',
  'trl',
];

export function StartupsPage(): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { can } = useAuth();
  const { state, toggleSort, setPage, setPageSize, setFilter, clearFilters } =
    useTableState(SORT_FIELDS, PRESERVED_KEYS);

  const query = useMemo(
    () => ({
      page: state.page,
      pageSize: state.pageSize,
      ...state.filters,
      ...(state.sort === null
        ? {}
        : { sort: `${state.sort.field}:${state.sort.direction}` }),
    }),
    [state],
  );

  const { data, isPending, isError } = useStartups(query);

  /* The view lives in the URL alongside the filters, so a shared link opens on
     the view the sender was looking at, and a reload does not switch someone
     back to a view they had left. */
  const view: PipelineView = state.filters['view'] === 'board' ? 'board' : 'table';

  /* `view` shares the query string with the filters but is not one: counting it
     would tell the user a filter is applied when they have only switched
     views, and "Clear filters" would then appear with nothing to clear. */
  const activeFilters = useMemo(() => {
    const { view: _view, ...rest } = state.filters;
    return rest;
  }, [state.filters]);

  const columns: readonly Column<StartupSummary>[] = useMemo(
    () => [
      {
        key: 'name',
        header: t('startups.columns.name'),
        cell: (row) => row.name,
        sortField: 'name',
        card: 'primary',
      },
      {
        key: 'stage',
        header: t('startups.columns.stage'),
        cell: (row) => <StageBadge stage={row.stage} />,
        sortField: 'stage',
        card: 'secondary',
      },
      {
        key: 'band',
        header: t('startups.columns.band'),
        cell: (row) => <BandBadge band={row.priorityBand} />,
        sortField: 'priorityBand',
        card: 'secondary',
      },
      {
        key: 'daysInStage',
        header: t('startups.columns.daysInStage'),
        /* The stalled flag rides on the same cell as the count, because the
           number alone does not say whether it is a problem — and PRD §9.7
           forbids conveying that with colour alone. */
        cell: (row) =>
          row.isStalled ? (
            <Badge tone="warn">{t('startup.stalled')}</Badge>
          ) : (
            formatNumber(row.daysInStage)
          ),
        sortField: 'stageEnteredAt',
        numeric: true,
        card: 'secondary',
      },
      {
        key: 'country',
        header: t('startups.columns.country'),
        cell: (row) => row.hqCountry ?? '—',
        card: 'hidden',
      },
      {
        key: 'trl',
        header: t('startups.columns.trl'),
        cell: (row) => (row.trl === null ? '—' : formatNumber(row.trl)),
        sortField: 'trl',
        numeric: true,
        card: 'hidden',
      },
      {
        key: 'raised',
        header: t('startups.columns.raised'),
        cell: (row) => <Money value={row.totalRaisedEur} />,
        numeric: true,
        card: 'hidden',
      },
    ],
    [t],
  );

  if (isError) {
    return (
      <p role="alert" className="text-sem-bad">
        {t('errors.generic')}
      </p>
    );
  }

  const hasFilters = Object.keys(activeFilters).length > 0;

  return (
    <>
      <PageHead
        kicker={t('nav.group.pipeline')}
        title={t('startups.title')}
        /* The count is the page's most useful single fact, so it sits in the
           head rather than only in the pagination footer. Absent while the
           first query is in flight — a zero there would read as an empty
           pipeline. */
        {...(data === undefined
          ? {}
          : { lead: t('startups.count', { count: data.meta.total }) })}
        {...(can('EDITOR')
          ? {
              actions: (
                /* `action` — the prototype's green create button, the one
                   place chrome may spend the design system's green share. */
                <Button asChild variant="action">
                  <Link to={ROUTES.startupNew}>{t('startups.new')}</Link>
                </Button>
              ),
            }
          : {})}
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <StartupFilters
            filters={activeFilters}
            onFilterChange={setFilter}
            onClear={clearFilters}
          />
        </div>
        {/* Export mirrors the on-screen filters and sort; hidden from Viewers
            (PRD §4, F18). `view` is a UI toggle, not a list filter, so it is
            not sent. */}
        <ExportButton entity="startups" query={{ ...activeFilters, ...(state.sort === null ? {} : { sort: `${state.sort.field}:${state.sort.direction}` }) }} />
        <ViewToggle
          view={view}
          onViewChange={(next) => {
            /* The table is the default, so it is stored as an absent value —
               keeping a pristine URL clean. */
            setFilter('view', next === 'table' ? '' : next);
          }}
        />
      </div>

      {view === 'board' ? (
        <BoardView
          startups={data?.data ?? []}
          isPending={isPending}
          onOpen={(id) => {
            void navigate(startupPath(id));
          }}
        />
      ) : (
        <div className="border-gray-100 rounded-lg border bg-white">
          <DataTable<StartupSummary>
            columns={columns}
            rows={data?.data ?? []}
            rowKey={(row) => row.id}
            caption={t('startups.title')}
            sort={state.sort}
            onSortChange={toggleSort}
            onRowSelect={(row) => {
              void navigate(startupPath(row.id));
            }}
            isLoading={isPending}
            /* Different message when filters are applied: "nothing logged yet"
             is misleading when the user has filtered everything out. */
            emptyMessage={hasFilters ? t('startups.empty') : t('startups.emptyAll')}
          />

          {data === undefined ? null : (
            <Pagination
              page={data.meta.page}
              pageSize={data.meta.pageSize}
              total={data.meta.total}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          )}
        </div>
      )}
    </>
  );
}

/**
 * The board, with its stage mutation wired in.
 *
 * Split out so the loading state is handled before the board mounts: dnd-kit
 * measures its droppables on mount, and mounting it against an empty list
 * would have it measure a board that is about to change.
 */
function BoardView({
  startups,
  isPending,
  onOpen,
}: {
  startups: readonly StartupSummary[];
  isPending: boolean;
  onOpen: (id: string) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { can } = useAuth();
  const changeStage = useChangeStage();
  const { conflict, reportIfConflict, clear: clearConflict } = useConflict();
  const queryClient = useQueryClient();

  if (isPending) {
    return (
      <p role="status" className="text-body text-sm">
        {t('state.loading')}
      </p>
    );
  }

  return (
    <>
      <StartupBoard
        startups={startups}
        onOpen={onOpen}
        canEdit={can('EDITOR')}
        onMove={async (startupId, stage, acknowledge) => {
          /* Found from the same list the board renders, so it is always present.
           Asserted rather than defaulted: a `?? 0` version would send a stale
           guard the API then rejects, turning a real move into a spurious
           409. */
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- see above
          const startup = startups.find((candidate) => candidate.id === startupId)!;

          try {
            /* The version the caller read: the API rejects a stale one with a
             409, which is what surfaces a colleague's concurrent edit
             (PRD §6.2). */
            await changeStage.mutateAsync({
              startupId,
              stage,
              version: startup.version,
              acknowledgeWarnings: acknowledge,
            });

            return [];
          } catch (error) {
            /* Unmet exit criteria arrive as a 422 carrying the list. PRD §5.3
             makes this a warning rather than a block, so the warnings are
             returned for the dialog to show — not rethrown as a failure. */
            if (isApiError(error) && error.is(ProblemType.EXIT_CRITERIA_UNMET)) {
              /* Asserted, not defaulted: the server raises this status only with
               a non-empty list, so a `?? []` would be an unreachable branch. */
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- see above
              return [...error.problem.warnings!];
            }

            /* A 409 gets the conflict dialog rather than the board's generic
             "could not be moved": that message says nothing about what
             happened or what to do, and the user would drag the card again
             with the same stale version (PRD §6.2). */
            if (reportIfConflict(error)) return [];

            throw error;
          }
        }}
      />

      <ConflictDialog
        open={conflict !== null}
        submittedVersion={conflict?.submittedVersion}
        onDismiss={clearConflict}
        onReload={() => {
          clearConflict();
          /* Invalidating rather than reloading the page: the conflict is about
             one card, and a page reload would lose the user's filters. */
          void queryClient.invalidateQueries({ queryKey: queryKeys.startups.all });
        }}
      />
    </>
  );
}
